/**
 * Whose local copy a store holds (g71): an account's, or one shared list's as
 * one visitor sees it through one link.
 *
 * Each scope is its own database, so nothing a link brings in can reach an
 * account's Lists, Today or Upcoming, and nothing queued under one link or one
 * visitor can go out under another. A view link and an edit link of the same
 * list are two scopes, and so are two visitors in one browser, and a link
 * before and after it was regenerated.
 *
 * The token is a credential. A scope carries it in memory, for the transport's
 * header, and nothing here writes it anywhere: the database name and the
 * registry below hold the grant id, which the server already answers with as
 * `generation` and which opens nothing without the token's signature.
 *
 * A list the visitor added to their Lists (g132) is opened without a link: by
 * its grant id and the account's session. It is the same scope, and the same
 * database, as the link opens for that account, so the two share one copy,
 * one queue and one set of unsent changes.
 */

import type { ResolvedShare, ShareCredential, ShareMode, ShareVisitor } from "../lib/sharing.js";

export type { ShareCredential };

export interface AccountScope {
  kind: "account";
  userId: string;
}

export interface SharedScope {
  kind: "shared";
  credential: ShareCredential;
  listId: string;
  mode: ShareMode;
  /** The grant the link names. Regenerating a link makes a new one. */
  generation: string;
  /** The visitor's creator id, or null for someone who has not given a name. */
  visitor: string | null;
  visitorKind: ShareVisitor["kind"];
  /**
   * The signed-in account this copy was opened for, when the visitor is an
   * account (g132). An added list's grant id is no secret, so its copy
   * reopens only for this account, never for whoever has the browser next.
   */
  account?: string;
}

export type StoreScope = AccountScope | SharedScope;

/** The scope a resolved link or added list opens, for the visitor the server says is looking. */
export function sharedScope(credential: ShareCredential, resolved: ResolvedShare, account?: string): SharedScope {
  return {
    kind: "shared",
    credential,
    listId: resolved.list.id,
    mode: resolved.permission,
    generation: resolved.generation,
    visitor: resolved.visitor.creatorId,
    visitorKind: resolved.visitor.kind,
    ...(resolved.visitor.kind === "account" && account ? { account } : {}),
  };
}

/** What a shared page names as the visitor who queued a batch. */
export function visitorKey(scope: SharedScope): string {
  return scope.visitor ?? "anonymous";
}

/**
 * The database a scope lives in. An account's name is exactly what it was
 * before scopes existed, so every mirror and queue already on a device opens
 * as it did.
 */
export function scopeDatabaseName(scope: StoreScope): string {
  if (scope.kind === "account") return `trayflux:${scope.userId}`;
  return `${SHARED_PREFIX}${scope.listId}:${scope.mode}:${scope.generation}:${visitorKey(scope)}`;
}

export const SHARED_PREFIX = "trayflux:shared:v1:";

/** A stable, non-secret name for a scope, for anything a page keeps per scope. */
export function scopeKey(scope: StoreScope): string {
  return scopeDatabaseName(scope);
}

/**
 * The shared scopes this browser holds a database for. Kept so a link opened
 * again offline finds its copy without asking the server who is looking, and
 * so a scope that has ended can be cleared later.
 */
export interface RegisteredScope {
  db: string;
  listId: string;
  mode: ShareMode;
  generation: string;
  visitor: string | null;
  visitorKind: ShareVisitor["kind"];
  account?: string;
  lastUsed: number;
}

const REGISTRY = "trayflux:shared-scopes";

function read(): RegisteredScope[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? (parsed as RegisteredScope[]).filter((entry) => typeof entry?.db === "string") : [];
  } catch {
    return [];
  }
}

function write(entries: RegisteredScope[]): void {
  try {
    if (entries.length === 0) localStorage.removeItem(REGISTRY);
    else localStorage.setItem(REGISTRY, JSON.stringify(entries));
  } catch {
    // A browser that will not keep this still works; it just cannot reopen
    // a shared list offline.
  }
}

export function registeredScopes(): RegisteredScope[] {
  return read();
}

export function rememberSharedScope(scope: SharedScope, now = Date.now()): void {
  const db = scopeDatabaseName(scope);
  const { credential: _secret, kind: _kind, ...fields } = scope;
  write([...read().filter((entry) => entry.db !== db), { db, ...fields, lastUsed: now }]);
}

export function forgetSharedScope(db: string): void {
  write(read().filter((entry) => entry.db !== db));
}

/** The grant a token names. Not a credential on its own: the signature is what opens it. */
export function grantOf(token: string): string | undefined {
  return /^tfs_([A-Za-z0-9_-]{22})\./.exec(token)?.[1];
}

/**
 * The scope this link last opened in this browser, for opening it offline.
 * The most recently used one, since a visitor who has named themselves since
 * an anonymous visit has moved on to their named scope. An added list is only
 * ever opened by the account that added it, so it reopens only a copy opened
 * for `account`, the one this browser is signed in to, and never a guest's.
 */
export function rememberedSharedScope(credential: ShareCredential, account?: string): SharedScope | undefined {
  const generation = credential.kind === "link" ? grantOf(credential.token) : credential.grantId;
  if (!generation) return undefined;
  const entry = read()
    .filter((row) => row.generation === generation
      && (credential.kind === "link" || (row.visitorKind === "account" && account !== undefined && row.account === account)))
    .sort((a, b) => b.lastUsed - a.lastUsed)[0];
  if (!entry) return undefined;
  return {
    kind: "shared",
    credential,
    listId: entry.listId,
    mode: entry.mode,
    generation: entry.generation,
    visitor: entry.visitor,
    visitorKind: entry.visitorKind,
    ...(entry.account ? { account: entry.account } : {}),
  };
}
